import { useRouter } from 'next/router'
import * as React from 'react'
import FocusLock from 'react-focus-lock'

import {
  MediaAsContents,
  Search as SearchIcon,
  SelectMark as SelectMarkIcon,
  useViewportBreakpoints,
} from '@syconium/little-miss-figgy'

import { useSearchOverlayContext } from '../../../../../containers/SearchOverlayContext'
import { NoResultsText } from '../../SearchPage/styles'
import { SearchOverlayResults } from '../SearchOverlayResults'

import {
  A11bNumberOfResults,
  ClearButton,
  CloseButton,
  ErrorText,
  Form,
  IconWrapper,
  Input,
  InputBox,
  ResultsDropdown,
  ResultsWrapper,
  SearchButton,
  SearchWrapper,
} from './styles'
import { useViewModel } from './useViewModel'

type IProps = ReturnType<typeof useViewModel> & {
  resultsInFrontOfBackdrop: boolean
}

export const Template = React.memo<IProps>(
  ({
    autoCompleteResults,
    handleFormChange,
    handleSubmit,
    hasError,
    inputPlaceholder,
    inputRef,
    isInputInvalid,
    onClearFormClick,
    onClickSearchOverlayProduct,
    popularSearchResults,
    popularSearchTitle,
    resetForm,
    resultsInFrontOfBackdrop,
    searchButtonAriaLabelClear,
    searchButtonAriaLabelSubmit,
    searchTextError,
    searchTextValue: searchText,
    showNoResultsMessage,
  }) => {
    const isSearching = searchText.length > 0
    const route = useRouter()
    const device = useViewportBreakpoints()
    const isSearchPage = route && route.pathname.includes('search')
    const isDropDownMenuClosed = popularSearchResults.length === 0
    const { shouldDisplaySearchOverlay } = useSearchOverlayContext()
    const isMobileView = device === 'small'

    return (
      <SearchWrapper isSearching={isSearching} isSearchPage={isSearchPage}>
        <FocusLock
          disabled={
            (isSearchPage && isDropDownMenuClosed) || (isDropDownMenuClosed && isMobileView)
          }
        >
          <SearchWrapper isSearching={isSearching} isSearchPage={isSearchPage}>
            <Form autoComplete='off' noValidate onSubmit={handleSubmit} role='search'>
              <InputBox isInvalid={isInputInvalid}>
                <Input
                  aria-label={inputPlaceholder}
                  name='searchText'
                  onChange={handleFormChange}
                  placeholder={inputPlaceholder}
                  ref={inputRef}
                  value={searchText}
                />
                <MediaAsContents lessThan='md'>
                  <SearchButton
                    aria-label={searchButtonAriaLabelSubmit}
                    onClick={() => resetForm()}
                    type='button'
                  >
                    <IconWrapper>
                      <SearchIcon />
                    </IconWrapper>
                  </SearchButton>
                  {isSearching && (
                    <ClearButton
                      aria-label={searchButtonAriaLabelClear}
                      onClick={() => resetForm()}
                      type='button'
                    >
                      <IconWrapper>
                        <SelectMarkIcon height='26px' width='26px' viewbox='-5 -5 20 20' />
                      </IconWrapper>
                    </ClearButton>
                  )}
                </MediaAsContents>

                <MediaAsContents greaterThanOrEqual='md'>
                  <SearchButton
                    aria-label={
                      isSearching ? searchButtonAriaLabelClear : searchButtonAriaLabelSubmit
                    }
                    onClick={() => resetForm()}
                    type='button'
                  >
                    <IconWrapper>
                      {isSearching ? (
                        <SelectMarkIcon height='26px' width='26px' viewbox='-5 -5 20 20' />
                      ) : (
                        <SearchIcon />
                      )}
                    </IconWrapper>
                  </SearchButton>
                  {shouldDisplaySearchOverlay && (
                    <CloseButton
                      aria-label={searchButtonAriaLabelClear}
                      onClick={() => resetForm()}
                      type='button'
                    >
                      <SelectMarkIcon height='26px' width='26px' viewbox='-5 -5 20 20' />
                    </CloseButton>
                  )}
                </MediaAsContents>
              </InputBox>
              {searchTextError ? (
                <ErrorText role='alert' aria-live='assertive' isInvalid={isInputInvalid}>
                  {searchTextError}
                </ErrorText>
              ) : null}
            </Form>
            {showNoResultsMessage && <NoResultsText>No results for '{searchText}'</NoResultsText>}
            <A11bNumberOfResults role='alert'>
              {searchText.length > 0 ? `${autoCompleteResults.length} results` : null}
            </A11bNumberOfResults>
            {showNoResultsMessage || searchText.length > 0 ? (
              <ResultsWrapper>
                <ResultsDropdown resultsInFrontOfBackdrop={resultsInFrontOfBackdrop}>
                  <SearchOverlayResults
                    {...{
                      autoCompleteResults,
                      hasError,
                      onClearFormClick,
                      onClickSearchOverlayProduct,
                      popularSearchResults,
                      popularSearchTitle,
                      searchText,
                    }}
                  />
                </ResultsDropdown>
              </ResultsWrapper>
            ) : null}
          </SearchWrapper>
        </FocusLock>
      </SearchWrapper>
    )
  }
)
